import { HttpStatusCode } from '@angular/common/http';
import { ADYEN_ERROR } from '@constants/adyen-error.constants';
import { defer, MonoTypeOperatorFunction, retry, throwError, timer } from 'rxjs';

/** True when cakephp refused a `/book` because another request for the same slot holds its booking lock (HTTP 423). */
export function isBookingLockError(error: { status?: number } | null | undefined): boolean {
  return error?.status === HttpStatusCode.Locked;
}

/**
 * Re-sends a `/book` request while it is answered with a booking lock, at fixed offsets from the
 * FIRST lock answer, the last one after the lock has certainly expired. It is the same request,
 * re-subscribed, so the payload (and the payment session it carries) is unchanged and no new payment
 * is ever started here. The first answer that is not a lock - a booking, "already booked", any other
 * refusal - reaches the subscriber as it came; so does the lock answer to the last resend.
 *
 * Each subscription keeps its own clock, so a page that books twice never shares a schedule.
 */
export function resendWhileBookingLocked<T>(
  resendAtMs: readonly number[] = ADYEN_ERROR.BOOKING_LOCK.RESEND_AT_MS,
): MonoTypeOperatorFunction<T> {
  return source => defer(() => {
    let firstLockAt: number | undefined;

    return source.pipe(retry<T>({
      count: resendAtMs.length,
      delay: (error: { status?: number } | null | undefined, retryCount: number) => {
        if (!isBookingLockError(error)) {
          return throwError(() => error);
        }
        firstLockAt ??= Date.now();

        return timer(Math.max(0, firstLockAt + resendAtMs[retryCount - 1] - Date.now()));
      },
    }));
  });
}
